// ─── White-label tenant layer (member app side) ───
// Everything here is gated on `org != null`; solo users never hit this code path
// beyond a single `me` call that returns { org: null }.
//
// API: every action is POST /api/org { action, ...payload } with the session
// token header (apiPost adds X-Session-Token). See WHITELABEL_PLAN.md §4 and
// ironlog-api/api/org/index.mjs for the response shapes.
import { createContext, useContext } from 'react';
import { apiPost, ApiError } from './api';
import { LS } from './storage';
import { V } from './theme';

export const ORG_CACHE_KEY = "ft-org";
export const PENDING_JOIN_KEY = "ft-pending-join";
export const OPEN_INBOX_KEY = "ft-open-org-inbox";

// Feature keys (mirror FEATURE_KEYS in the API). All default to true.
export const FLAG_KEYS = ['social', 'feed', 'friends', 'groups', 'messages_social', 'league', 'duels', 'ai_coach', 'ai_chat', 'programs',
  'nutrition', 'meal_plan', 'fast_food', 'supplements', 'photos', 'form_check', 'share_cards', 'youtube_videos',
  'ecosystem_links', 'donate_card', 'install_banner', 'messages'];
export const ALL_FLAGS_ON = Object.freeze(Object.fromEntries(FLAG_KEYS.map(k => [k, true])));

// Tabs that belong to a flag: reaching one via hash/shortcut while the flag is off redirects home.
export const TAB_FLAGS = {
  plan_coach: 'ai_coach', plan_aichat: 'ai_chat', plan_programs: 'programs', plan_mealplan: 'meal_plan', plan_fastfood: 'fast_food',
  plan_supplements: 'supplements', track_photos: 'photos', track_compare: 'photos', plan_formcheck: 'form_check',
  log_nutrition: 'nutrition', track_nutrition: 'nutrition',
  social_feed: 'feed', social_friends: 'friends', social_groups: 'groups', social_messages: 'messages_social',
  social_league: 'league', social_duels: 'duels', org_inbox: 'messages',
};

// Order matters: it is the order of the toggles (mirrors SCOPES in the API).
export const SCOPES = [
  { key: 'workouts', label: 'Workouts', desc: 'Sessions, sets, reps and RPE' },
  { key: 'checkins', label: 'Check-ins', desc: 'Sleep, energy, soreness and how you felt after sessions' },
  { key: 'nutrition', label: 'Nutrition and macros', desc: 'Calories, protein, carbs, fat, water' },
  { key: 'body', label: 'Body weight and measurements', desc: 'Weight, body fat and tape measurements' },
  { key: 'photos', label: 'Progress photos, never your private ones', desc: 'Photos you marked private are never shared' },
];
export const CONSENT_DEFAULTS = Object.freeze({ share_workouts: true, share_checkins: true, share_nutrition: false, share_body: false, share_photos: false });

// ─── API ───
export const orgApi = (action, payload = {}, opts = {}) => apiPost('/api/org', { action, ...payload }, opts);
// Public actions (no session): preview_invite
export const orgApiPublic = (action, payload = {}) => apiPost('/api/org', { action, ...payload }, { auth: false });

// Normalise an ApiError into { status, code, ...extra }
export function orgErr(e) {
  if (e instanceof ApiError) {
    const body = e.body && typeof e.body === 'object' ? e.body : {};
    return { status: e.status, code: body.error || (e.status === 0 ? 'network' : 'error'), ...body };
  }
  return { status: 0, code: 'network', message: e?.message };
}

export function errorMessage(err, orgName) {
  const org = orgName || err?.org_name || 'this gym';
  switch (err?.code) {
    case 'invalid_invite': return "This invite link isn't valid. Ask your coach for a new one.";
    case 'invite_expired': return 'This invite has expired. Ask your coach for a new link.';
    case 'invite_exhausted': return 'This invite has already been used. Ask your coach for a new link.';
    case 'invite_revoked': return 'This invite was cancelled by the gym. Ask your coach for a new link.';
    case 'already_in_org': return `You're already in ${org}; leave it first in Settings → Your gym.`;
    case 'invite_email_mismatch': return 'This invite was sent to a different email address. Sign in with the address your coach used.';
    case 'seat_limit_reached': return `${org} has no free seats right now. Ask your coach to make room.`;
    case 'org_suspended': return "This gym's account is paused. Your data is safe and still yours.";
    case 'org_features_disabled': return 'Gym features are temporarily unavailable. Try again later.';
    case 'owner_cannot_leave': return 'Owners cannot leave their own gym.';
    case 'super_admin_required': return `Only the super-admin account can do this. ${err?.hint === 'ADMIN_EMAIL is not set on the API' ? 'Set ADMIN_EMAIL on the API (Vercel → ironlog-api → Environment Variables) to your email and redeploy.' : 'The ADMIN_EMAIL variable on the API (Vercel → ironlog-api) must exactly match the email you are signed in with; fix it and redeploy.'}`;
    case 'forbidden': return "You don't have permission to do that.";
    case 'unauthorized': case 'verification_required': return 'Please sign in again to continue.';
    case 'rate_limited': return 'Too many requests — try again in a minute.';
    case 'feature_disabled': return 'Your gym has turned this feature off.';
    case 'invalid_seat_limit': return 'Seat limit must be a whole number between 1 and 100000';
    // Phase G: scheduled announcements / templates / notification prefs
    case 'not_author': return 'Only the person who wrote this can change it.';
    case 'not_scheduled': return "This announcement isn't scheduled, so there is nothing to cancel.";
    case 'unknown_pref': return "That notification setting isn't recognised. Update the app and try again.";
    case 'invalid_pref': return 'Notification settings can only be on or off.';
    case 'too_many_templates': return 'Template limit reached — delete one before saving another.';
    // Super-admin panel (org-scoped actions)
    case 'not_member': return "That account isn't an active member of this organization.";
    case 'cannot_change_owner': case 'cannot_remove_owner': return "The owner's role can't be changed here — transfer ownership first.";
    case 'already_assigned': return 'That coach is already assigned to this client.';
    case 'not_a_coach': return 'That account is not a coach in this organization.';
    case 'not_a_client': return 'Only members (not staff) can be assigned to a coach.';
    case 'org_not_found': return 'That organization no longer exists.';
    case 'no_org': return 'This action needs an organization context.';
    case 'invalid_cursor': return 'The page cursor is stale — reload the list.';
    case 'invalid_date': return 'Enter a valid date range.';
    case 'invalid_action_filter': return 'Action filters may only contain lowercase letters, digits and underscores.';
    case 'network': return "Couldn't reach the server. Check your connection and try again.";
    default: return err?.code ? `Something went wrong (${err.code}).` : 'Something went wrong.';
  }
}

// ─── Consent failures ───
// A failed `set_consent` must never be treated as a saved choice: redeeming an invite
// already grants workouts and check-ins on the server, so closing the sheet on an error
// leaves the coach with access the member just switched off.
//   kind 'no_org'      — there is no membership to record consent against; nothing was
//                        granted either, so the sheet can close (not retryable).
//   kind 'unavailable' — transient (offline, 5xx, rate limit, features off, suspended):
//                        keep the member's selection and offer a retry.
//   kind 'error'       — anything else: not retryable, but the member may continue.
// `note` is the plain-language line the UI shows so "continue" is never mistaken for "saved".
export function consentFailure(err) {
  const status = err?.status, code = err?.code;
  if (status === 404 || code === 'no_org' || code === 'not_member') return { kind: 'no_org', retry: false, note: '' };
  const retry = status === 0 || status === 423 || status === 429 || status >= 500 ||
    code === 'network' || code === 'org_features_disabled' || code === 'org_suspended';
  return {
    kind: retry ? 'unavailable' : 'error',
    retry,
    note: retry
      ? "Your choices aren't saved yet. Try again, or continue and set them in Settings → Your gym."
      : "Your choices aren't saved yet. You can set them in Settings → Your gym.",
  };
}

// ─── Cache ───
export function readOrgCache() {
  const c = LS.get(ORG_CACHE_KEY);
  return c && typeof c === 'object' && c.org ? c : null;
}
export function writeOrgCache(me) {
  if (!me || !me.org) { LS.del(ORG_CACHE_KEY); return null; }
  const rec = { ...me, cached_at: new Date().toISOString() };
  delete rec.stale;
  LS.set(ORG_CACHE_KEY, rec);
  return rec;
}
export function clearOrgCache() { LS.del(ORG_CACHE_KEY); }

export const isActiveOrg = (me) => !!(me && me.org && me.org.status === 'active');
export const isSuspendedOrg = (me) => !!(me && me.org && me.org.status !== 'active');

// The brand members see: the org's saved branding, with the organization's name
// standing in for a missing app name so a gym that has only named itself still
// shows that name in the header. Null for solo users and suspended orgs.
export const memberBrand = (me) => {
  if (!isActiveOrg(me)) return null;
  const name = typeof me.org.name === 'string' && me.org.name.trim() ? me.org.name.trim() : null;
  if (me.branding) return me.branding.app_name ? me.branding : { ...me.branding, app_name: name };
  return name ? { app_name: name } : null;
};

// Effective flags: server-merged `flags` when in an active org, else everything on.
export function flagsFrom(me) {
  if (!isActiveOrg(me)) return ALL_FLAGS_ON;
  const out = { ...ALL_FLAGS_ON };
  const f = me.flags && typeof me.flags === 'object' ? me.flags : {};
  for (const k of FLAG_KEYS) if (typeof f[k] === 'boolean') out[k] = f[k];
  return out;
}

// ─── React context ───
export const OrgContext = createContext({
  org: null, raw: null, suspended: false, stale: false, resolved: false,
  flags: ALL_FLAGS_ON, unread: { messages: 0, announcements: 0 },
  setUnread: () => {}, refreshOrg: async () => null,
});
export const useOrg = () => useContext(OrgContext);
export const useFlags = () => useContext(OrgContext).flags;

// Non-hook accessor for deep leaf components (share buttons, YouTube links) —
// the provider keeps this snapshot in sync; all-on when no org.
let _flagsSnapshot = ALL_FLAGS_ON;
export const setFlagsSnapshot = (f) => { _flagsSnapshot = f || ALL_FLAGS_ON; };
export const flagOn = (key) => _flagsSnapshot[key] !== false;

// ─── Realtime ping bus ───
// App.jsx holds the single realtime subscription (one channel per topic) and
// re-emits pings here so OrgInbox can refetch without a second subscription.
const _pingSubs = new Set();
export const onOrgPing = (fn) => { _pingSubs.add(fn); return () => _pingSubs.delete(fn); };
export const emitOrgPing = (payload) => { _pingSubs.forEach(fn => { try { fn(payload || {}); } catch {} }); };

// ─── Dynamic manifest + apple-touch-icon ───
// Platform notes (see WHITELABEL_PLAN.md §5.3):
//  • Chrome/Android/desktop read the manifest at install time, so swapping the
//    <link rel=manifest> href to a blob: URL before the install prompt is enough.
//    That is why App.jsx defers the install banner until `me` has resolved.
//  • iOS Safari reads `apple-mobile-web-app-title` and `apple-touch-icon` at the
//    moment the user taps "Add to Home Screen" and freezes them: a member who
//    installed IronLog *before* joining keeps the IronLog icon/name until they
//    delete and re-add the app (the "Reinstall" banner in App.jsx covers this).
//    blob:/data: icons are unreliable on iOS, so we point at the R2 PNG URL.
//  • apple-touch-startup-image needs one image per device size — out of scope;
//    iOS shows the background colour instead.
const DEFAULT_MANIFEST_HREF = './manifest.json';
const DEFAULT_APPLE_ICON = './icon-192.png';
const DEFAULT_TITLE = 'IRONLOG';
const SHORTCUTS = [
  { name: 'Log Workout', short_name: 'Workout', description: 'Start logging a new workout', url: './#log_workout' },
  { name: 'Log Nutrition', short_name: 'Nutrition', description: "Track today's meals and macros", url: './#log_nutrition', flag: 'nutrition' },
  { name: 'Weigh In', short_name: 'Weigh In', description: 'Log body weight and measurements', url: './#log_body' },
  { name: 'AI Coach', short_name: 'Coach', description: "View today's adaptive program", url: './#track_coach', flag: 'ai_coach' },
];

export function buildManifest(branding, flags = ALL_FLAGS_ON) {
  const b = branding || {};
  const name = b.app_name || DEFAULT_TITLE;
  const short = b.short_name || name.slice(0, 12);
  const icons = [];
  if (b.icon_192_url) icons.push({ src: b.icon_192_url, sizes: '192x192', type: 'image/png', purpose: 'any' });
  if (b.icon_512_url) icons.push({ src: b.icon_512_url, sizes: '512x512', type: 'image/png', purpose: 'any' });
  if (!icons.length) {
    icons.push({ src: 'icon-192.png', sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: 'icon-512.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
      { src: 'icon.svg', sizes: 'any', type: 'image/svg+xml', purpose: 'any maskable' });
  }
  const iconFor = icons[0] ? [{ src: icons[0].src, sizes: icons[0].sizes }] : undefined;
  return {
    id: '/', name, short_name: short,
    description: `${name} — workouts, nutrition and progress in one place.`,
    start_url: './', scope: './', lang: 'en', dir: 'ltr', display: 'standalone',
    display_override: ['standalone'], orientation: 'portrait',
    // theme_color must agree with the theme-color meta (brand.js sets it to V.bg for the current mode)
    background_color: b.bg_dark || '#08080d', theme_color: V.bg,
    categories: ['health', 'fitness', 'lifestyle'],
    icons,
    shortcuts: SHORTCUTS.filter(s => !s.flag || flags[s.flag] !== false).map(({ flag: _f, ...s }) => ({ ...s, icons: iconFor })),
    launch_handler: { client_mode: ['focus-existing', 'auto'] },
    scope_extensions: [{ origin: 'https://ironlog.space' }, { origin: 'https://www.ironlog.space' }],
  };
}

let _manifestBlobUrl = null;
export function applyManifest(branding, flags) {
  if (typeof document === 'undefined') return;
  const link = document.getElementById('manifest-link');
  const apple = document.getElementById('apple-touch-icon-link');
  const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  const absolute = (u) => { try { return new URL(u, window.location.href).href; } catch { return u; } };
  if (branding && branding.app_name) {
    try {
      const manifest = buildManifest(branding, flags);
      // Relative icon paths would resolve against blob: — make them absolute
      manifest.icons = manifest.icons.map(i => ({ ...i, src: absolute(i.src) }));
      manifest.shortcuts = manifest.shortcuts.map(s => ({ ...s, url: absolute(s.url), icons: s.icons?.map(i => ({ ...i, src: absolute(i.src) })) }));
      manifest.start_url = absolute(manifest.start_url); manifest.scope = absolute(manifest.scope);
      if (typeof Blob !== 'undefined' && typeof URL !== 'undefined' && URL.createObjectURL) {
        const url = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' }));
        if (_manifestBlobUrl) { try { URL.revokeObjectURL(_manifestBlobUrl); } catch {} }
        _manifestBlobUrl = url;
        if (link) link.setAttribute('href', url);
      }
    } catch {}
    if (apple && branding.apple_icon_url) { apple.setAttribute('href', branding.apple_icon_url); apple.setAttribute('sizes', '180x180'); }
    if (titleMeta) titleMeta.setAttribute('content', branding.app_name);
    document.title = branding.app_name;
  } else {
    if (link && link.getAttribute('href') !== DEFAULT_MANIFEST_HREF) link.setAttribute('href', DEFAULT_MANIFEST_HREF);
    if (apple && apple.getAttribute('href') !== DEFAULT_APPLE_ICON) { apple.setAttribute('href', DEFAULT_APPLE_ICON); apple.setAttribute('sizes', '192x192'); }
    if (titleMeta && titleMeta.getAttribute('content') !== DEFAULT_TITLE) titleMeta.setAttribute('content', DEFAULT_TITLE);
    if (document.title !== DEFAULT_TITLE) document.title = DEFAULT_TITLE;
    if (_manifestBlobUrl) { try { URL.revokeObjectURL(_manifestBlobUrl); } catch {} _manifestBlobUrl = null; }
  }
}

// ─── Small formatting helpers shared by the org UI ───
export const scopeLabel = (scope) => (SCOPES.find(s => s.key === scope) || { label: scope }).label;
export const shortScope = (scope) => ({ workouts: 'workouts', checkins: 'check-ins', nutrition: 'nutrition', body: 'body measurements', photos: 'progress photos', all: 'everything' })[scope] || scope;
export const sourceLabel = (src) => ({ invite: 'via invite', settings: 'in Settings', coach_request: 'coach request', revoke_all: 'stop sharing', leave: 'left gym', removed: 'removed' })[src] || src;
export const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }); } catch { return ''; } };
export const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
export const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso), now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? fmtTime(iso) : fmtDay(iso);
};
export const personName = (p) => (p && (p.name || p.first_name || (p.email || '').split('@')[0])) || 'Coach';
